import { Alerts } from '@features/researches/models/alert.model';
import { ReportStatus, ResearchReports } from '@features/researches/models/report.model';
import {
  CreatorOrganisationalStructure,
  CreatorResearchDetails,
  CreatorSpacePlan,
  CreatorSpaceUtilization,
  CreatorStep,
  CreatorWorkspaceDetails,
  ResearchCreator,
  StepStatus
} from '@features/researches/models/research-creator.model';
import { AvailableFloor, ResearchSensorsSetup, ResearchStatus } from '@features/researches/models/research.model';
import { AllSensors } from '@features/researches/models/sensor.model';
import {
  activateResearchSuccess,
  addIndustry,
  archiveResearchSuccess,
  changeOrganisationalStructureStepToNext,
  changeOrganisationalStructureStepToPrevious,
  changeReportStatus,
  changeResearchDetailsStepToNext,
  changeSpacePlanStepToPrevious,
  changeSpaceUtilizationStepToNext,
  changeSpaceUtilizationStepToPrevious,
  changeWorkspaceDetailsStepToNext,
  changeWorkspaceDetailsStepToPrevious,
  clearReportResearchData,
  clearResearchPreview,
  completeDeviceSetupSuccess,
  editResearchSuccess,
  getAlertsDateRangeSuccess,
  getAllSensorsSuccess,
  getDataForReportGenerationSuccess,
  getIndustriesSuccess,
  getResearchesSuccess,
  getResearchForDraftEditSuccess,
  getResearchForPreviewSuccess,
  getResearchForSensorsSetupSuccess,
  getResearchParticipantsSuccess,
  getResearchReportsSuccess,
  getResearchSpacePlanSuccess,
  getSensorsAlertsSuccess,
  getSensorsStatisticsSuccess,
  getTechnicianNotificationsSuccess,
  getTimezonesSuccess,
  initResearchCreator,
  saveCompanyLogo,
  saveResearchDraft,
  saveResearchDraftSuccess,
  saveResearchSetup,
  saveResearchSetupFailure,
  saveResearchSetupSuccess,
  saveSpacePlan,
  setupSensorSuccess,
  unpairSensorSuccess
} from '@features/researches/store/researches.actions';
import { ResearchesState } from '@features/researches/store/researches.state';
import {
  isSpacePlanFileSizeValid,
  mapResearchToCreator,
  mapResearchToSensorDataForAdditionalRooms,
  mapResearchToSensorDataForUtilisedSpace
} from '@features/researches/utils/reseraches.utils';
import { Action, createReducer, on } from '@ngrx/store';
import { ReportType } from '@shared/models/report.model';

export function researchesReducer(state: ResearchesState | undefined, action: Action): ResearchesState {
  return reducer(state, action);
}

export const initialState: ResearchesState = {
  researches: [],
  alerts: {} as Alerts,
  industries: [],
  researchCreator: {} as ResearchCreator,
  allSensors: {} as AllSensors,
  loading: false,
  timezones: undefined
};
export const initialCreatorState: ResearchCreator = {
  previousStatus: ResearchStatus.DRAFT,
  details: {
    status: StepStatus.IN_PROGRESS
  } as CreatorResearchDetails,
  workspace: {
    status: StepStatus.TO_DO
  } as CreatorWorkspaceDetails,
  structure: {
    status: StepStatus.TO_DO
  } as CreatorOrganisationalStructure,
  utilization: {
    status: StepStatus.TO_DO
  } as CreatorSpaceUtilization,
  plan: {
    status: StepStatus.TO_DO
  } as CreatorSpacePlan
};

const reducer = createReducer(
  initialState,
  on(initResearchCreator, (state) => ({
    ...state,
    researchCreator: initialCreatorState
  })),
  on(changeResearchDetailsStepToNext, (state, action) => {
    if (!state.researchCreator) {
      return state;
    }
    const creator = {
      ...state.researchCreator,
      previousStatus: ResearchStatus.DRAFT,
      details: { ...state.researchCreator.details, ...action.step },
      workspace: { ...state.researchCreator.workspace, status: StepStatus.IN_PROGRESS }
    };
    return {
      ...state,
      researchCreator: creator
    };
  }),
  on(changeWorkspaceDetailsStepToNext, (state, action) => {
    if (!state.researchCreator) {
      return state;
    }

    let utilization;
    if (Object.keys(state.researchCreator.utilization).length > 1) {
      utilization = {
        ...state.researchCreator.utilization,
        byTeams: state.researchCreator.utilization.byTeams?.map((byTeam) => ({
          ...byTeam,
          locations: byTeam.locations.map((location) => {
            if (action.step.availableFloors?.find((f: AvailableFloor) => f.externalId === location.floorExternalId)) {
              return location;
            } else {
              return { ...location, floorExternalId: null };
            }
          })
        }))
      } as CreatorSpaceUtilization;
    }

    const creator = {
      ...state.researchCreator,
      workspace: action.step,
      structure: { ...state.researchCreator.structure, status: StepStatus.IN_PROGRESS },
      utilization: utilization ? utilization : { ...state.researchCreator.utilization }
    };
    return {
      ...state,
      researchCreator: creator
    };
  }),
  on(changeWorkspaceDetailsStepToPrevious, (state, action) => {
    if (!state.researchCreator) {
      return state;
    }

    let utilization;
    if (Object.keys(state.researchCreator.utilization).length > 1 && action.step) {
      utilization = {
        ...state.researchCreator.utilization,
        byTeams: state.researchCreator.utilization.byTeams?.map((byTeam) => ({
          ...byTeam,
          locations: byTeam.locations.map((location) => {
            if (action.step?.availableFloors?.find((f: AvailableFloor) => f.externalId === location.floorExternalId)) {
              return location;
            } else {
              return { ...location, floorExternalId: null };
            }
          })
        }))
      } as CreatorSpaceUtilization;
    }
    const creator = {
      ...state.researchCreator,
      details: { ...state.researchCreator.details, status: StepStatus.IN_PROGRESS },
      workspace: action.step ? action.step : { ...state.researchCreator.workspace, status: StepStatus.TO_DO },
      utilization: utilization ? utilization : { ...state.researchCreator.utilization }
    };
    return {
      ...state,
      researchCreator: creator
    };
  }),
  on(changeOrganisationalStructureStepToNext, (state, action) => {
    if (!state.researchCreator) {
      return state;
    }

    let utilization;
    if (Object.keys(state.researchCreator.utilization).length > 1 && action.step) {
      const teams = state.researchCreator.utilization.byTeams.filter((team) =>
        action.step.departments
          .find((d) => d.externalId === team.departmentExternalId)
          ?.teams.find((t) => t.externalId === team.teamExternalId)
      );
      utilization = {
        ...state.researchCreator.utilization,
        status: StepStatus.IN_PROGRESS,
        byTeams: teams
      } as CreatorSpaceUtilization;
    }

    const creator = {
      ...state.researchCreator,
      structure: action.step,
      utilization: utilization ? utilization : { ...state.researchCreator.utilization, status: StepStatus.IN_PROGRESS }
    };
    return {
      ...state,
      researchCreator: creator
    };
  }),
  on(changeOrganisationalStructureStepToPrevious, (state, action) => {
    if (!state.researchCreator) {
      return state;
    }

    let utilization;
    if (Object.keys(state.researchCreator.utilization).length > 1 && action.step) {
      const teams = state.researchCreator.utilization.byTeams.filter((team) =>
        action.step?.departments
          .find((d) => d.externalId === team.departmentExternalId)
          ?.teams.find((t) => t.externalId === team.teamExternalId)
      );
      utilization = {
        ...state.researchCreator.utilization,
        byTeams: teams
      } as CreatorSpaceUtilization;
    }

    const creator = {
      ...state.researchCreator,
      workspace: { ...state.researchCreator.workspace, status: StepStatus.IN_PROGRESS },
      structure: action.step ? action.step : { ...state.researchCreator.structure, status: StepStatus.TO_DO },
      utilization: utilization ? utilization : { ...state.researchCreator.utilization }
    };
    return {
      ...state,
      researchCreator: creator
    };
  }),
  on(changeSpaceUtilizationStepToNext, (state, action) => {
    if (!state.researchCreator) {
      return state;
    }
    const creator = {
      ...state.researchCreator,
      utilization: action.step,
      plan: { ...state.researchCreator.plan, status: StepStatus.IN_PROGRESS }
    };
    return {
      ...state,
      researchCreator: creator
    };
  }),
  on(changeSpaceUtilizationStepToPrevious, (state, action) => {
    if (!state.researchCreator) {
      return state;
    }
    const creator = {
      ...state.researchCreator,
      structure: { ...state.researchCreator.structure, status: StepStatus.IN_PROGRESS },
      utilization: action.step
        ? action.step
        : {
            ...state.researchCreator.utilization,
            status: StepStatus.TO_DO
          }
    };
    return {
      ...state,
      researchCreator: creator
    };
  }),
  on(changeSpacePlanStepToPrevious, (state, action) => {
    if (!state.researchCreator) {
      return state;
    }
    const creator = {
      ...state.researchCreator,
      utilization: {
        ...state.researchCreator.utilization,
        status: StepStatus.IN_PROGRESS,
        byTeams: state.researchCreator.utilization.byTeams?.map((byTeam) => ({
          departmentExternalId: byTeam.departmentExternalId,
          teamExternalId: byTeam.teamExternalId,
          locations: byTeam.locations.map((location) => ({
            ...location,
            sensorData: action.utilisedSpacesSensors?.find((uss) => uss.teamId === byTeam.teamExternalId)?.sensorsData
              ? action.utilisedSpacesSensors?.find((uss) => uss.teamId === byTeam.teamExternalId)?.sensorsData
              : {}
          }))
        })),
        additionalRooms: state.researchCreator.utilization.additionalRooms?.map((additionalRoom) => ({
          ...additionalRoom
        }))
      },
      plan: {
        ...state.researchCreator.plan,
        status: !!state.researchCreator.plan.spacePlanFile ? StepStatus.COMPLETED : StepStatus.TO_DO
      }
    };
    return {
      ...state,
      researchCreator: creator
    };
  }),
  on(saveSpacePlan, (state, action) => {
    if (!state.researchCreator) {
      return state;
    }
    const creator = {
      ...state.researchCreator,
      plan: state.researchCreator.plan
        ? {
            ...state.researchCreator.plan,
            spacePlanFile: action.file,
            spacePlanFileName: action.fileName,
            spacePlanFileValid: isSpacePlanFileSizeValid(action.file)
          }
        : {
            status: StepStatus.IN_PROGRESS,
            spacePlanFile: action.file,
            spacePlanFileName: action.fileName,
            spacePlanFileValid: isSpacePlanFileSizeValid(action.file)
          }
    };
    return {
      ...state,
      researchCreator: creator
    };
  }),
  on(saveCompanyLogo, (state, action) => {
    if (!state.researchCreator) {
      return state;
    }
    return {
      ...state,
      researchCreator: {
        ...state.researchCreator,
        details: {
          ...state.researchCreator.details,
          companyLogoFile: action.file
        }
      }
    };
  }),
  on(saveResearchSetup, (state, action) => {
    if (!state.researchCreator) {
      return state;
    }
    const creator = {
      ...state.researchCreator,
      utilization: {
        ...state.researchCreator.utilization,
        byTeams: state.researchCreator.utilization.byTeams?.map((byTeam) => ({
          departmentExternalId: byTeam.departmentExternalId,
          teamExternalId: byTeam.teamExternalId,
          locations: byTeam.locations.map((location) => ({
            ...location,
            sensorData: action.utilisedSpacesSensors?.find(
              (uss) => uss.teamId === byTeam.teamExternalId && uss.locationId === location.externalId
            )?.sensorsData
              ? action.utilisedSpacesSensors?.find(
                  (uss) => uss.teamId === byTeam.teamExternalId && uss.locationId === location.externalId
                )?.sensorsData
              : {}
          }))
        })),
        additionalRooms: state.researchCreator.utilization.additionalRooms?.map((additionalRoom) => ({
          ...additionalRoom
        }))
      },
      plan: {
        ...state.researchCreator.plan,
        status: StepStatus.IN_PROGRESS
      }
    };
    return {
      ...state,
      researchCreator: creator,
      loading: true
    };
  }),
  on(saveResearchSetupSuccess, (state, action) => {
    if (!state.researchCreator) {
      return state;
    }
    const creator = {
      ...state.researchCreator,
      details: {
        ...state.researchCreator.details,
        id: action.researchId
      }
    };
    return {
      ...state,
      researchCreator: creator,
      loading: false
    };
  }),
  on(saveResearchSetupFailure, (state, action) => {
    return {
      ...state,
      loading: false
    };
  }),
  on(addIndustry, (state, action) => {
    return {
      ...state,
      industries: [...state.industries, action.industry]
    };
  }),
  on(getIndustriesSuccess, (state, action) => {
    return {
      ...state,
      industries: action.industries
    };
  }),
  on(getResearchesSuccess, (state, action) => {
    return {
      ...state,
      researches: action.researches
    };
  }),
  on(getResearchForPreviewSuccess, (state, action) => {
    return {
      ...state,
      researchPreview: action.research
    };
  }),
  on(saveResearchDraft, (state, action) => {
    if (!state.researchCreator) {
      return state;
    }
    let creator = {
      ...state.researchCreator,
      [action.stepName]: { ...state.researchCreator[action.stepName], ...action.step }
    };

    let utilization;
    if (Object.keys(creator.utilization).length > 1 && action.stepName === CreatorStep.STRUCTURE) {
      const teams = state.researchCreator.utilization.byTeams.filter((team) =>
        creator.structure.departments
          .find((d) => d.externalId === team.departmentExternalId)
          ?.teams.find((t) => t.externalId === team.teamExternalId)
      );
      utilization = {
        ...state.researchCreator.utilization,
        byTeams: teams
      } as CreatorSpaceUtilization;

      creator = {
        ...creator,
        utilization: utilization
      };
    }

    return {
      ...state,
      researchCreator: creator
    };
  }),
  on(saveResearchDraftSuccess, (state, action) => {
    if (!state.researchCreator) {
      return state;
    }
    const creator = {
      ...state.researchCreator,
      details: {
        ...state.researchCreator.details,
        id: action.researchId
      }
    };
    return {
      ...state,
      researchCreator: creator
    };
  }),
  on(getResearchForDraftEditSuccess, (state, action) => ({
    ...state,
    researchCreator: mapResearchToCreator(action.research, action.companyLogoFile, action.spacePlanFileName)
  })),
  on(getTechnicianNotificationsSuccess, (state, action) => {
    if (!state.researchPreview) {
      return state;
    }
    return {
      ...state,
      researchPreview: { ...state.researchPreview, technicianNotifications: action.notifications }
    };
  }),
  on(getResearchParticipantsSuccess, (state, action) => {
    if (!state.researchPreview) {
      return state;
    }
    return {
      ...state,
      researchPreview: { ...state.researchPreview, userNotifications: action.invitations }
    };
  }),
  on(getResearchReportsSuccess, (state, action) => {
    if (!state.researchPreview) {
      return state;
    }
    const monthly = action.reports.filter((report) => report.type === ReportType.MONTHLY);
    const final = action.reports.filter((report) => report.type === ReportType.FINAL);
    const quarterly = action.reports.filter((report) => report.type === ReportType.QUARTERLY);
    const other = action.reports.filter((report) => report.type === ReportType.OTHER);
    return {
      ...state,
      researchPreview: { ...state.researchPreview, reports: { monthly, final, quarterly, other } }
    };
  }),
  on(getResearchForSensorsSetupSuccess, (state, action) => {
    const sensorsForAdditionalRooms = mapResearchToSensorDataForAdditionalRooms(action.research);
    const sensorsForUtilisedSpace = mapResearchToSensorDataForUtilisedSpace(action.research);
    return {
      ...state,
      researchSensorsSetup: {
        research: action.research,
        sensorsData: [...sensorsForUtilisedSpace, ...sensorsForAdditionalRooms]
      }
    };
  }),
  on(getResearchSpacePlanSuccess, (state, action) => {
    if (action.section === 'setup') {
      if (!state.researchSensorsSetup) {
        return state;
      }
      return {
        ...state,
        researchSensorsSetup: {
          ...state.researchSensorsSetup,
          spacePlanFileUrl: action.spacePlanFileUrl
        }
      };
    } else if (action.section === 'preview') {
      if (!state.researchPreview) {
        return state;
      }
      return {
        ...state,
        researchPreview: {
          ...state.researchPreview,
          spacePlanFileName: action.spacePlanFileName,
          spacePlanFileUrl: action.spacePlanFileUrl
        }
      };
    } else {
      return state;
    }
  }),
  on(activateResearchSuccess, (state, action) => {
    if (!state.researchPreview) {
      return state;
    }
    return {
      ...state,
      researchPreview: {
        ...state.researchPreview,
        details: { ...state.researchPreview.details, status: ResearchStatus.ACTIVE }
      }
    };
  }),
  on(setupSensorSuccess, (state, action) => {
    if (!state.researchSensorsSetup) {
      return state;
    }
    const sensorData = state.researchSensorsSetup.sensorsData.map((sensor) => {
      if (sensor.id === action.id) {
        return {
          ...sensor,
          macAddress: action.macAddress,
          status: action.status
        };
      }
      return sensor;
    });
    return {
      ...state,
      researchSensorsSetup: {
        ...state.researchSensorsSetup,
        sensorsData: sensorData
      }
    };
  }),
  on(editResearchSuccess, (state, action) => ({
    ...state,
    researchCreator: mapResearchToCreator(
      action.research,
      action.companyLogoFile,
      action.spacePlanFileName,
      ResearchStatus.IN_CONFIGURATION,
      true
    )
  })),
  on(changeReportStatus, (state, action) => {
    let reports = state.researchPreview?.reports;
    if (!state.researchPreview || !reports) {
      return state;
    }
    reports = changeReportStatusByReportType(action.reportType, reports, action.newStatus, action.reportId);
    return {
      ...state,
      researchPreview: { ...state.researchPreview, reports }
    };
  }),

  on(completeDeviceSetupSuccess, (state, action) => {
    const researchSensorsSetup = state.researchSensorsSetup as ResearchSensorsSetup;
    return {
      ...state,
      researchSensorsSetup: {
        ...researchSensorsSetup,
        research: {
          ...researchSensorsSetup.research,
          details: { ...researchSensorsSetup.research.details, status: ResearchStatus.READY }
        }
      }
    };
  }),

  on(archiveResearchSuccess, (state, action) => {
    return {
      ...state,
      researches: state.researches.map((research) => {
        if (research.id === action.researchId) {
          return { ...research, status: ResearchStatus.ARCHIVED };
        }
        return research;
      })
    };
  }),
  on(getSensorsAlertsSuccess, (state, action) => {
    return {
      ...state,
      alerts: { ...state.alerts, data: action.alerts }
    };
  }),
  on(getAlertsDateRangeSuccess, (state, action) => {
    return {
      ...state,
      alerts: { ...state.alerts, dateRange: action.dateRange }
    };
  }),
  on(getAllSensorsSuccess, (state, action) => {
    return {
      ...state,
      allSensors: { ...state.allSensors, sensors: action.sensorsData }
    };
  }),
  on(getSensorsStatisticsSuccess, (state, action) => {
    return {
      ...state,
      allSensors: { ...state.allSensors, statistics: action.sensorsStatistics }
    };
  }),
  on(getDataForReportGenerationSuccess, (state, action) => {
    return {
      ...state,
      researchReport: action.reportData
    };
  }),
  on(clearReportResearchData, (state, action) => {
    return {
      ...state,
      researchReport: undefined
    };
  }),
  on(clearResearchPreview, (state, action) => {
    return {
      ...state,
      researchPreview: undefined
    };
  }),
  on(getTimezonesSuccess, (state, action) => {
    return {
      ...state,
      timezones: action.timezones
    };
  }),
  on(unpairSensorSuccess, (state, action) => {
    if (!state.researchSensorsSetup) {
      return state;
    }
    const sensorData = state.researchSensorsSetup.sensorsData.map((sensor) =>
      sensor.macAddress === action.sensorMacAddress
        ? {
            ...sensor,
            macAddress: '',
            status: undefined
          }
        : sensor
    );
    return {
      ...state,
      researchSensorsSetup: {
        ...state.researchSensorsSetup,
        sensorsData: sensorData
      }
    };
  })
);

function changeReportStatusByReportType(
  reportType: ReportType,
  reports: ResearchReports,
  newStatus: ReportStatus,
  reportId: number
): ResearchReports {
  if (reportType === ReportType.OTHER) {
    return {
      ...reports,
      other: reports.other.map((report) => {
        if (report.id === reportId) {
          return { ...report, status: newStatus };
        }
        return report;
      })
    };
  } else if (reportType === ReportType.MONTHLY) {
    return {
      ...reports,
      monthly: reports.monthly.map((report) => {
        if (report.id === reportId) {
          return { ...report, status: newStatus };
        }
        return report;
      })
    };
  } else if (reportType === ReportType.QUARTERLY) {
    return {
      ...reports,
      quarterly: reports.quarterly.map((report) => {
        if (report.id === reportId) {
          return { ...report, status: newStatus };
        }
        return report;
      })
    };
  } else if (reportType === ReportType.FINAL) {
    return {
      ...reports,
      final: reports.final.map((report) => {
        if (report.id === reportId) {
          return { ...report, status: newStatus };
        }
        return report;
      })
    };
  }
  return reports;
}
